import React, {useCallback, useContext, useState} from 'react';
import {useDispatch} from 'react-redux';

import {MessageContext} from 'src/contexts/message-context';
import {downloadReportById} from 'src/openapi/admin/apis/UsageReportsV2Api';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Loading from '@pb/kit-component-loading';
import {LocaleContext} from '@pb/kit-context-locale';

import type {GeneratedReport} from '../../typedef';
import type {ApiDispatchForToolkit} from 'src/openapi/admin/types';


type Props = {
    reportId: string,
    onOpenReport: (report: GeneratedReport) => void,

    fileId?: string,
    fileName?: string,
    generatedAt?: string,
};

export const FileNameCell = ({fileId, fileName, generatedAt, reportId, onOpenReport}: Props) => {
    const apiDispatch = useDispatch<ApiDispatchForToolkit>();
    const [isDownloading, setIsDownloading] = useState(false);
    const {setSuccessMessage, setErrorMessage} = useContext(MessageContext);
    const {locale} = useContext(LocaleContext);

    const localeDateFormat = mapDateFormat(locale.dateFormat, 'NUMERIC');


    const downloadFile = useCallback(async () => {
        if (fileId && !isDownloading) {
            setIsDownloading(true);
            const response = await apiDispatch(downloadReportById({reportId: fileId}));
            setIsDownloading(false);

            if (response.payload.error) {
                setErrorMessage(response.payload.data.message || 'Failed to download report. Please, try again later.');
            } else {
                setSuccessMessage('The file has been downloaded successfully');
            }
        }
    }, [fileId, apiDispatch, setErrorMessage, setSuccessMessage, isDownloading]);

    const openReport = useCallback(() => {
        if (generatedAt && reportId) {
            onOpenReport({reportId, generatedAt});
        }
    }, [reportId, onOpenReport, generatedAt]);

    if (fileId && fileName) {
        return (
            <div style={{marginLeft: 24}}>
                <Group alignItems="center" gap="xs">
                    {isDownloading
                        ? <Loading type="ICON" fast={true}/>
                        : <Icon type={Icons.EXCEL}/>}
                    <EllipsisTextHint title={fileName}>
                        <a onClick={downloadFile}>{fileName}</a>
                    </EllipsisTextHint>
                </Group>
            </div>
        );
    }

    return (
        <EllipsisTextHint
            title={`Usage Report on ${FormattedDate.format({
                value: generatedAt,
                format: `${localeDateFormat} HH:mm [at] A`})
            }`}>
            <a onClick={openReport}>
                <b>
                    Usage Report on {FormattedDate.format({
                        value: generatedAt,
                        format: `${localeDateFormat} HH:mm [at] A`,
                    })}
                </b>
            </a>
        </EllipsisTextHint>
    );
};
